import { type FC, useState, useEffect } from 'react';
import { ExternalLink } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/Card';
import { supabase } from '@/lib/api/supabase';
import type { Sponsor } from '@/types';
import { dbToSponsor } from '@/types';

/**
 * Sponsors & Vendors page component
 */
export const SponsorsPage: FC = () => {
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [vendors, setVendors] = useState<Sponsor[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSponsors = async (): Promise<void> => {
      try {
        setLoading(true);

        const { data, error } = await supabase
          .from('sponsors')
          .select('*')
          .eq('visible', true)
          .order('display_order', { ascending: true });

        if (error) {
          console.error('Failed to fetch sponsors:', error);
          return;
        }

        if (data) {
          const allSponsors = data.map(dbToSponsor);
          setSponsors(allSponsors.filter(s => s.type === 'sponsor'));
          setVendors(allSponsors.filter(s => s.type === 'vendor'));
        }
      } catch (err) {
        console.error('Error fetching sponsors:', err);
      } finally {
        setLoading(false);
      }
    };

    void fetchSponsors();
  }, []);

  const renderSponsorCard = (sponsor: Sponsor) => (
    <Card key={sponsor.id} className="overflow-hidden hover:shadow-lg transition-shadow">
      <CardContent className="p-6">
        <div className="flex flex-col items-center text-center space-y-4">
          {sponsor.logoUrl ? (
            <img
              src={sponsor.logoUrl}
              alt={sponsor.name}
              className="h-24 w-auto object-contain"
            />
          ) : (
            <div className="h-24 flex items-center">
              <h3 className="text-2xl font-bold">{sponsor.name}</h3>
            </div>
          )}

          {sponsor.tier && sponsor.type === 'sponsor' && (
            <span
              className="px-3 py-1 rounded-full text-xs font-semibold uppercase text-white"
              style={{ backgroundColor: sponsor.color || '#14b8a6' }}
            >
              {sponsor.tier.charAt(0).toUpperCase() + sponsor.tier.slice(1)} Sponsor
            </span>
          )}

          {sponsor.description && (
            <p className="text-sm text-muted-foreground">{sponsor.description}</p>
          )}

          {sponsor.boothNumber && (
            <p className="text-sm font-medium">Booth #{sponsor.boothNumber}</p>
          )}

          {sponsor.websiteUrl && (
            <a
              href={sponsor.websiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-primary hover:underline"
            >
              Visit Website
              <ExternalLink className="h-4 w-4" />
            </a>
          )}
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="bg-gradient-to-b from-turquoise-800 via-turquoise-700 to-turquoise-800 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-orange-500 border-r-transparent"></div>
          <p className="mt-4 text-white">Loading sponsors...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-b from-turquoise-800 via-turquoise-700 to-turquoise-800 min-h-screen">
      <div className="container mx-auto px-4 py-12">
        <div className="mb-12 text-center">
          <h1 className="text-4xl font-viking text-white mb-4 drop-shadow-lg">
            Sponsors & Vendors
          </h1>
          <p className="text-lg text-orange-200">
            Thank you to our amazing supporters who make Cactus Cup possible!
          </p>
        </div>

        {/* Sponsors Section */}
        {sponsors.length > 0 && (
          <section className="mb-12">
            <h2 className="text-3xl font-viking text-white mb-6 text-center">Our Sponsors</h2>

            {/* Cholla Sponsors - $1200+ */}
            {sponsors.filter(s => s.tier === 'cholla').length > 0 && (
              <div className="mb-8">
                <h3 className="text-2xl font-viking text-fuchsia-300 mb-4 text-center">Cholla Sponsors</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                  {sponsors.filter(s => s.tier === 'cholla').map(renderSponsorCard)}
                </div>
              </div>
            )}

            {/* Creosote Sponsors - $900-$1199 */}
            {sponsors.filter(s => s.tier === 'creosote').length > 0 && (
              <div className="mb-8">
                <h3 className="text-2xl font-viking text-lime-300 mb-4 text-center">Creosote Sponsors</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                  {sponsors.filter(s => s.tier === 'creosote').map(renderSponsorCard)}
                </div>
              </div>
            )}

            {/* Opuntia Sponsors - $600-$899 */}
            {sponsors.filter(s => s.tier === 'opuntia').length > 0 && (
              <div className="mb-8">
                <h3 className="text-2xl font-viking text-amber-300 mb-4 text-center">Opuntia Sponsors</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {sponsors.filter(s => s.tier === 'opuntia').map(renderSponsorCard)}
                </div>
              </div>
            )}

            {/* Saguaro Sponsors - $300-$599 */}
            {sponsors.filter(s => s.tier === 'saguaro').length > 0 && (
              <div className="mb-8">
                <h3 className="text-2xl font-viking text-emerald-300 mb-4 text-center">Saguaro Sponsors</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {sponsors.filter(s => s.tier === 'saguaro').map(renderSponsorCard)}
                </div>
              </div>
            )}

            {/* Ocotillo Sponsors - $1-$299 */}
            {sponsors.filter(s => s.tier === 'ocotillo').length > 0 && (
              <div className="mb-8">
                <h3 className="text-2xl font-viking text-rose-300 mb-4 text-center">Ocotillo Sponsors</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  {sponsors.filter(s => s.tier === 'ocotillo').map(renderSponsorCard)}
                </div>
              </div>
            )}
          </section>
        )}

        {/* Vendors Section */}
        {vendors.length > 0 && (
          <section>
            <h2 className="text-3xl font-viking text-white mb-6 text-center">Vendors</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {vendors.map(renderSponsorCard)}
            </div>
          </section>
        )}

        {sponsors.length === 0 && vendors.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">
                Sponsor and vendor information will be available soon.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};
